/**
 * Auto-paginación de secciones del dossier para el Modo Presentación 16:9.
 *
 * Caso real: el "Tratamiento documental" de Grabarte tiene 9.810 caracteres.
 * Si lo metemos en un único slide con line-clamp se trunca a ~700c y se pierde
 * el 93% del texto. Solución: partir automáticamente la sección en N slides,
 * cada uno con una porción del texto + header "Sinopsis · 2 de 4".
 *
 * La paginación respeta:
 *   1) Saltos de párrafo dobles (\n\n) — preferentes
 *   2) Saltos de frase (. ! ?) si un párrafo es muy grande
 *   3) Saltos de espacio si una frase es enorme (raro)
 *
 * Capacidad de cada slide según el layout (en caracteres aproximados):
 */
export const LAYOUT_CAPACITY = {
  // Capacidad pequeña — sólo texto + imagen
  "half-split":         700,
  "text-image-right":   750,
  "text-image-left":    750,
  "portrait-quote":     350,  // solo 1ª frase + un poco
  "director-statement": 850,
  "team-individual":    700,
  "fullbleed":          200,  // muy poco texto, casi nada
  // Capacidad media — texto solo
  "text":               900,
  "text-narrow":        800,
  // Capacidad grande — 2 columnas
  "two-cols":          1500,
  // No paginables (no muestran texto largo)
  "cover":              0,
  "chapter-divider":    0,  // separador, solo título
  "quote":              0,  // solo 1ª frase
  "items":              0,
  "grid-2x2":           0,
  "stats-grid":         0,
  "mood-photo":         0,  // solo imagen
  // Plantillas nuevas (Feb 2026) — todas son layouts visuales-estructurales
  // que NO se paginan por texto largo (cada una tiene su propio contenido)
  "timeline":           0,  // hitos discretos
  "comparison":         0,  // 2 cajas independientes
  "references-grid":    0,  // 4 pósters
  "credits-grid":       0,  // 6 personas
  "data-callout":       0,  // 1 cifra protagonista
};

/** Devuelve la capacidad de chars del layout. 0 = no pagina. */
export function layoutCapacity(layout) {
  return LAYOUT_CAPACITY[layout] ?? 700;
}

/**
 * Parte un texto largo en N porciones respetando límites naturales.
 * Si capacidad === 0 o el texto cabe → devuelve [texto] (1 sola parte).
 */
export function paginateText(text, capacity) {
  if (!text || !capacity || text.length <= capacity) {
    return [text || ""];
  }
  // 1) Cortar por párrafos dobles
  const paragraphs = text.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  const parts = [];
  let buffer = "";

  const flush = () => {
    if (buffer.trim()) parts.push(buffer.trim());
    buffer = "";
  };

  for (const para of paragraphs) {
    // ¿Cabe el párrafo entero en lo que queda del buffer actual?
    const candidateLen = buffer.length + (buffer ? 2 : 0) + para.length;
    if (candidateLen <= capacity) {
      buffer = buffer ? buffer + "\n\n" + para : para;
      continue;
    }
    // No cabe junto al buffer. ¿El párrafo solo cabe en una slide nueva?
    if (para.length <= capacity) {
      flush();
      buffer = para;
      continue;
    }
    // El párrafo SOLO también excede la capacidad → partir por frases
    flush();
    const sentences = para.split(/(?<=[.!?])\s+/);
    let sbuf = "";
    for (const s of sentences) {
      if (sbuf.length + 1 + s.length <= capacity) {
        sbuf = sbuf ? sbuf + " " + s : s;
      } else if (s.length <= capacity) {
        if (sbuf) parts.push(sbuf.trim());
        sbuf = s;
      } else {
        // Frase enorme → cortar por palabras
        if (sbuf) { parts.push(sbuf.trim()); sbuf = ""; }
        let wbuf = "";
        for (const w of s.split(/\s+/)) {
          if (wbuf.length + 1 + w.length <= capacity) {
            wbuf = wbuf ? wbuf + " " + w : w;
          } else {
            if (wbuf) parts.push(wbuf.trim());
            wbuf = w;
          }
        }
        if (wbuf) sbuf = wbuf;
      }
    }
    if (sbuf) buffer = sbuf;
  }
  flush();
  return parts.length > 0 ? parts : [text];
}

/**
 * Cuenta cuántas partes generaría una sección con su layout efectivo.
 * Útil para el Style Linter del editor del dossier.
 */
export function countParts(section, effectiveLayout) {
  const capacity = layoutCapacity(effectiveLayout);
  if (!capacity) return 1;
  const text = section?.text || "";
  if (text.length <= capacity) return 1;
  return paginateText(text, capacity).length;
}
